import React from 'react';
import { Box, Button, Chip, Paper, Stack, Typography, CircularProgress, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VitalGraphView from './VitalGraphView';

// Shared presentation only. The live container supplies its existing handlers;
// onboarding supplies example data and inert/no-op handlers. No data hooks here.
export default function WellnessOverviewView({ badgeColor, clampScore, customFrom, customTo, handleLegendClick, latest, loading, range, scans, scoreLabel, setAppliedFrom, setAppliedTo, setCustomFrom, setCustomTo, setRange, setShowBottomTrends, setShowTopTrends, showBottomTrends, showTopTrends, trendData, visibleMetrics, vitalCards, vitalDateRange, vitalGraphData, vitalStatusColor, vitalStatusLabel, wellnessRadarData }) {
  return (<Paper
        elevation={0}
        sx={{
            p: 3,
            borderRadius: 2,
            border: '1px solid #e6e6e6',
            backgroundColor: '#ffffff',
        }}
        >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Wellness Overview
            </Typography>

            <Stack direction="row" spacing={1}>
                {[
                { key: 'last', label: 'Last Scan' },
                { key: 'week', label: 'This Week' },
                { key: 'month', label: 'This Month' },
                { key: 'custom', label: 'Custom' },
                ].map((btn) => (
                <Button
                    key={btn.key}
                    size="small"
                    onClick={() => {
                    setRange(btn.key);
                    if (btn.key === 'custom') {
                        setCustomFrom('');
                        setCustomTo('');
                        setAppliedFrom('');
                        setAppliedTo('');
                    }
                    }}
                    variant={range === btn.key ? 'contained' : 'outlined'}
                    sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2,
                    backgroundColor: range === btn.key ? '#29C0BB' : '#F5F7FA',
                    color: range === btn.key ? '#fff' : '#1F2933',
                    borderColor: '#E5E7EB',
                    '&:hover': {
                        backgroundColor: range === btn.key ? '#23B3AE' : '#EEF2F7',
                    },
                    }}
                >
                    {btn.label}
                </Button>
                ))}
            </Stack>
        </Stack>

        {range === 'custom' && (
        <Box sx={{ mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
            <TextField
                type="date"
                size="small"
                label="Start date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
            />
            <TextField
                type="date"
                size="small"
                label="End date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
            />
            <Button
                size="small"
                variant="contained"
                onClick={() => {
                setAppliedFrom(customFrom);
                setAppliedTo(customTo);
                }}
                sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#29C0BB',
                }}
            >
                Apply
            </Button>
            <Button
                size="small"
                variant="outlined"
                onClick={() => {
                setCustomFrom('');
                setCustomTo('');
                setAppliedFrom('');
                setAppliedTo('');
                }}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
                Reset
            </Button>
            </Stack>
        </Box>
        )}

        {loading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
            </Box>
        ) 
        :
        scans.length === 0 ? (
            <Box
            sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: '#F9FAFB',
                border: '1px dashed #E5E7EB',
                textAlign: 'center',
            }}
            >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                No scans yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
                There are no wellness scans available for this patient.
            </Typography>
            </Box>
        ) 
        :  
        (
            <>
            {/* <Box
                sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: '#E8F9F6',
                border: '1px solid #CFF0EA',
                mb: 3,
                }}
            >
                <Typography variant="body2">
                <strong>Good:</strong> Overall wellness indicators are good. Most metrics are within
                healthy ranges. Focus on maintaining current habits while addressing relaxation scores.
                </Typography>
            </Box> */}

            <Grid data-tour="demo-dashboard-wellness-scores" container spacing={3} alignItems="stretch">
                <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, backgroundColor: '#F9FAFB' }}>
                    <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={wellnessRadarData}>
                        <PolarGrid stroke="#e0e0e0" />
                        <PolarAngleAxis
                            dataKey="subject"
                            tick={{ fill: '#666', fontSize: 10 }}
                        />
                        <PolarRadiusAxis
                            angle={90}
                            domain={[0, 5]}
                            ticks={[0, 1, 2, 3, 4, 5]}
                            tick={{ fill: '#666', fontSize: 10, dy: 4, dx: 6}}
                            axisLine={false}
                            tickCount={6}
                            allowDecimals={false}
                        />
                        <Radar
                            dataKey="value"
                            stroke="#26a69a"
                            fill="#26a69a"
                            fillOpacity={0.4}
                            strokeWidth={2}
                            isAnimationActive={false}
                        />
                    </RadarChart>
                    </ResponsiveContainer>
                </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                <Grid container spacing={2}>
                    {[
                    { label: 'Activity', value: latest?.activity },
                    { label: 'Sleep', value: latest?.sleep },
                    { label: 'Equilibrium', value: latest?.equilibrium },
                    { label: 'Metabolism', value: latest?.metabolism },
                    { label: 'Resilience', value: latest?.health },
                    { label: 'Relaxation', value: latest?.relaxation },
                    ].map((item) => {
                    const score = clampScore(item.value);
                    const label = scoreLabel(score);
                    return (
                        <Grid key={item.label} size={{ xs: 12, sm: 6 }}>
                        <Paper
                            elevation={0}
                            sx={{
                            p: 2,
                            borderRadius: 2,
                            border: label === 'Fair' ? '1px solid #F59E0B' : '1px solid #EEF2F7',
                            backgroundColor: label === 'Fair' ? '#FFF7ED' : '#F9FAFB',
                            }}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {item.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: badgeColor(label), fontWeight: 700 }}>
                                {label}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'right' }}>
                                {score.toFixed(1)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                / 5
                                </Typography>
                            </Box>
                            </Stack>
                        </Paper>
                        </Grid>
                    );
                    })}
                </Grid>
                </Grid>
            </Grid>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowTopTrends((v) => !v)}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    startIcon={<TrendingUpIcon />}
                >
                    {showTopTrends ? 'Hide Trends' : 'View Trends'}
                </Button>
            </Stack>

            {showTopTrends && (
            <Paper data-tour="demo-dashboard-wellness-trends" elevation={0} sx={{ p: 2, borderRadius: 2, backgroundColor: '#F9FAFB', mb: 3 }}>
                <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis domain={[0, 5]} />
                    <Tooltip />
                    {/* Add onClick and cursor pointer style */}
                    <Legend onClick={handleLegendClick} wrapperStyle={{ cursor: 'pointer' }} />
                    <Line type="monotone" dataKey="activity" name="Activity" stroke="#3B82F6" dot={{ r: 3 }} hide={!visibleMetrics.activity} />
                    <Line type="monotone" dataKey="sleep" name="Sleep" stroke="#FF7F50" dot={{ r: 3 }} hide={!visibleMetrics.sleep} />
                    <Line type="monotone" dataKey="equilibrium" name="Equilibrium" stroke="#8B5CF6" dot={{ r: 3 }} hide={!visibleMetrics.equilibrium} />
                    <Line type="monotone" dataKey="metabolism" name="Metabolism" stroke="#F59E0B" dot={{ r: 3 }} hide={!visibleMetrics.metabolism} />
                    <Line type="monotone" dataKey="resilience" name="Resilience" stroke="#059669" dot={{ r: 3 }} hide={!visibleMetrics.resilience} />
                    <Line type="monotone" dataKey="relaxation" name="Relaxation" stroke="#EF4444" dot={{ r: 3 }} hide={!visibleMetrics.relaxation} />
                </LineChart>
                </ResponsiveContainer>
            </Paper>
            )}

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Vital Signs
            </Typography>

            <Grid data-tour="demo-dashboard-wellness" container spacing={2} sx={{ mb: 2 }}>
                {vitalCards.map((card) => (
                <Grid key={card.label} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, backgroundColor: '#F9FAFB', height: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="h4" sx={{ mb: 1 }}>{card.icon}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {card.label}
                    </Typography>
                    {card.label === 'Stress Index' ? (
                        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, color: vitalStatusColor(card.status).text }}>
                        {vitalStatusLabel(card.status)}
                        </Typography>
                    ) : (
                        <>
                        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.5 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {card.value}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                            {card.unit}
                            </Typography>
                        </Stack>
                        <Chip
                        label={vitalStatusLabel(card.status)}
                        size="small"
                        sx={{
                            mt: 1,
                            backgroundColor: vitalStatusColor(card.status).bg,
                            color: vitalStatusColor(card.status).text,
                            fontWeight: 600,
                        }}
                        />
                        </>
                    )}
                    </Paper>
                </Grid>
                ))}
            </Grid>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1, mb: 1 }}>
                <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowBottomTrends((v) => !v)}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    startIcon={<TrendingUpIcon />}
                >
                    {showBottomTrends ? 'Hide Trends' : 'View Trends'}
                </Button>
            </Stack>
            {showBottomTrends && (
                <VitalGraphView data={vitalGraphData} dateRange={vitalDateRange} />
            )}
            </>
        )}
        </Paper>);
}
